import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';

import { GETTERS as PRINT_GETTERS } from '@/store/modules/print/const';
import Layouts from '@/components/ToolPopovers/Layout';

import {
  CUSTOM_LAYOUT_TYPE,
  EDITION,
  PRINT_LAYOUT_TYPES,
  SAVED_AND_FAVORITES_TYPE,
  MODAL_TYPES,
  LAYOUT_SIZE_TYPES,
  ASSORTED_TYPE_VALUE,
  CONTENT_MAPPING_MODAL,
  SHEET_TYPE,
  PER_PAGE_10,
  LAYOUT_PAGE_TYPE
} from '@/common/constants';
import {
  getThemeOptSelectedById,
  isEmpty,
  insertItemsToArray,
  removeItemsFormArray,
  isHalfSheet,
  isHalfRight,
  isCoverLayoutChecker
} from '@/common/utils';
import { getItem, setItem } from '@/common/storage';
import {
  usePopoverCreationTool,
  useLayoutPrompt,
  useModal,
  useActionLayout,
  useCustomLayout,
  useLayoutElements,
  useGetLayouts,
  useAppCommon
} from '@/hooks';

import { getThemeByIdApi, getThemesApi } from '@/api/theme';

import {
  changeObjectsCoords,
  isFullLayoutChecker,
  leftRightObjectsOfLayout
} from '@/common/utils/layout';

export default {
  components: {
    Layouts
  },
  setup() {
    const edition = EDITION.PRINT;

    const { setToolNameSelected, selectedToolName } = usePopoverCreationTool();
    const { modalData, toggleModal } = useModal();
    const {
      isPrompt,
      updateVisited,
      setIsPrompt,
      pageSelected,
      themeId: defaultThemeId
    } = useLayoutPrompt(edition);

    const { getLayoutElements } = useLayoutElements();

    const {
      saveToFavorites,
      deleteFavorites,
      getFavoriteLayouts,
      getFavorites
    } = useActionLayout();
    const { getCustom } = useCustomLayout();
    const {
      getPrintLayouts,
      getAssortedLayoutCategories,
      getPrintLayoutByType,
      getAssortedLayouts
    } = useGetLayouts();
    const { setNotification } = useAppCommon();

    return {
      isPrompt,
      selectedToolName,
      setToolNameSelected,
      updateVisited,
      setIsPrompt,
      pageSelected,
      defaultThemeId,
      getPrintLayouts,
      getAssortedLayoutCategories,
      getPrintLayoutByType,
      modalData,
      toggleModal,
      saveToFavorites,
      deleteFavorites,
      getCustom,
      getFavoriteLayouts,
      getLayoutElements,
      setNotification,
      getAssortedLayouts,
      getFavorites
    };
  },
  data() {
    const textDisplay = {
      promptMsg:
        'The best way to get started is by selecting a layout. As a shortcut, the layouts from your selecting theme will be presented first.',
      promptTitle: 'Select a Layout',
      title: 'Layouts',
      optionTitle: 'Layout Type:'
    };
    return {
      themesOptions: [],
      layoutTypesOrigin: [],
      layoutTypes: [],
      disabledTheme: false,
      layoutTypeSelected: { sub: '' },
      themeSelected: {},
      textDisplay,
      layoutId: null,
      favoriteLayoutIds: [],
      customLayouts: [],
      layouts: [],
      extraLayouts: [],
      bookId: this.$route.params.bookId,
      currentPage: 1,
      isLoadingCategory: true,
      perPages: PER_PAGE_10,
      currentPageExtraLayouts: 1,
      isLoadingExtraLayouts: false,
      isLoadingLayouts: false,
      currentPageAssortedLayout: 1,
      tabType: null,
      currentPageFavLayout: 1,
      isLoadingTheme: true,
      currentPageTheme: 1,
      currentPageSpreadLayout: 1,
      currentPageSingleLayout: 1,
      currentPageUserSpreadLayouts: 0,
      currentPageUserSingleLayouts: 0
    };
  },
  computed: {
    ...mapGetters({
      totalBackground: PRINT_GETTERS.TOTAL_BACKGROUND,
      printObject: PRINT_GETTERS.GET_OBJECTS
    }),
    isVisited() {
      return this.pageSelected?.isVisited;
    },
    themeId() {
      return this.defaultThemeId;
    },
    isHalfSheet() {
      return this.pageSelected && isHalfSheet(this.pageSelected);
    },
    tabActive() {
      this.tabType = this.isHalfSheet ? 0 : 1;
      return this.isHalfSheet ? 1 : 0;
    }
  },
  watch: {
    pageSelected: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal?.id !== oldVal?.id) {
          this.initData();
        }
      }
    },
    layoutTypeSelected: {
      deep: true,
      handler(newVal) {
        if ([CUSTOM_LAYOUT_TYPE, ASSORTED_TYPE_VALUE].includes(newVal.value)) {
          this.disabledTheme = true;
          return;
        }
        this.disabledTheme = false;
      }
    }
  },
  async mounted() {
    await Promise.all([
      this.initPrintData(),
      this.getFavoritesData(),
      this.getAssorted()
    ]);

    this.getLayoutTypes();

    await this.filterLayoutType();

    await this.initData();
  },
  methods: {
    /**
     * Set up inital data to render in view
     */
    async initData() {
      await this.setThemeSelected(this.themeId);
      this.setLayoutSelected();

      await this.getLayouts();
    },
    /**
     * Set up inital data to render in view
     */
    async initPrintData() {
      this.themesOptions = await getThemesApi(false, this.currentPageTheme);
    },
    /**
     * Set default selected for layout base on id of sheet: Cover, Single Page or Collage
     */
    setLayoutSelected() {
      const type = this.layoutTypes[0];
      this.layoutTypeSelected = this.getSelectedType(type);
    },

    /**
     * Set default selected for theme base on id of sheet. Use default theme when the sheet not have private theme
     * @param  {Number} currentSheetThemeId Theme id of the current sheet
     */
    async setThemeSelected(currentSheetThemeId) {
      if (currentSheetThemeId) {
        const themeOpt = getThemeOptSelectedById(
          this.themesOptions,
          currentSheetThemeId
        );
        this.themeSelected = themeOpt;
        if (!themeOpt) {
          const theme = await getThemeByIdApi(currentSheetThemeId, false);
          if (!isEmpty(theme)) {
            this.themesOptions.push(theme);
            this.themeSelected = theme;
          } else {
            const notification = {
              isShow: true,
              type: 'warning',
              title: 'Warning',
              text: 'Please select a theme for this book'
            };
            this.setNotification({ notification });
            this.themeSelected = this.themesOptions[0];
          }
        }
        return;
      }

      const themeSelected = this.themesOptions.find(
        t => t.id === this.defaultThemeId
      );
      this.themeSelected = themeSelected;
    },
    /**
     * Set object theme selected from dropdown
     * @param {Object} theme theme that is selecting in the theme select box
     */
    async onChangeTheme(theme) {
      this.themeSelected = theme;

      this.setLayoutSelected();
      this.currentPageSingleLayout = 1;
      this.currentPageSpreadLayout = 1;
      await this.getLayouts();
    },
    /**
     * Set object layout selected from dropdown
     * @param {Object} layout layout type that is selecting in the layout type box
     */
    async onChangeLayoutType(layout) {
      this.layoutTypeSelected = this.getSelectedType(layout);
      this.currentPageExtraLayouts = 1;
      this.currentPageAssortedLayout = 1;
      this.currentPageFavLayout = 1;
      this.currentPageSingleLayout = 1;
      this.currentPageSpreadLayout = 1;
      this.currentPageUserSingleLayouts = 0;
      this.currentPageUserSpreadLayouts = 0;

      await this.getLayouts();
    },
    /**
     * Trigger hooks to set tool name is empty and then close popover when click Cancel button
     */
    onCancel() {
      const isHideModal = getItem(CONTENT_MAPPING_MODAL + this.bookId) || false;
      if (!isHideModal) {
        this.toggleModal({
          isOpenModal: true,
          modalData: {
            type: MODAL_TYPES.CONTENT_MAPPING
          }
        });
        setItem(CONTENT_MAPPING_MODAL + this.bookId, true);
      }
      this.setToolNameSelected('');
    },
    /**
     * Trigger mutation to set theme and layout for sheet after that close popover when click Select button
     * @param {Object} layoutData layout that is selected
     */
    async setThemeLayoutForSheet(layoutData) {
      if (
        (isEmpty(this.layouts) && isEmpty(this.extraLayouts)) ||
        !layoutData?.id
      )
        return;

      const layout = cloneDeep(layoutData);
      layout.objects = await this.getLayoutElements(layout.id);

      const isFullTemplate = isFullLayoutChecker(layout);

      if (isFullTemplate && this.isHalfSheet) {
        // remove left or right objects so that spread layout can be applied on half sheet
        const isInsideFrontCover = isHalfRight(this.pageSelected);

        const sizeType = isCoverLayoutChecker(layout)
          ? LAYOUT_SIZE_TYPES.HARD
          : LAYOUT_SIZE_TYPES.NORMAL;
        const { leftObjects, rightObjects } = leftRightObjectsOfLayout(
          layout.objects,
          sizeType
        );

        layout.objects = isInsideFrontCover ? rightObjects : leftObjects;
      }
      // change objects coords if user at FRONT_COVER or BACK_COVER
      if (this.isHalfSheet && !isFullTemplate) {
        layout.objects = changeObjectsCoords(
          layout.objects,
          this.pageSelected.type === SHEET_TYPE.FRONT_COVER ? 'right' : 'left'
        );
      }

      this.toggleModal({
        isOpenModal: true,
        modalData: {
          type: MODAL_TYPES.APPLY_LAYOUT,
          props: {
            themeId: this.themeSelected?.id,
            layout: layout
          }
        }
      });
      this.setToolNameSelected('');
    },

    /**
     * Trigger mutation set prompt false and update isVisited true for current sheet
     */
    onClickGotIt() {
      this.setIsPrompt({
        isPrompt: false
      });
      this.updateVisited({
        sheetId: this.pageSelected?.id
      });
    },

    /**
     * Save / unsave the selected layout to favorites
     *
     * @param {String | Number} id id of selected layout
     */
    async onSaveToFavorites({ id, isFavorites }) {
      const isSuccess = isFavorites
        ? await this.saveToFavorites(id)
        : await this.deleteFavorites(id);

      if (!isSuccess) return;

      this.modifyFavorites(id);

      if (this.layoutTypeSelected.value !== SAVED_AND_FAVORITES_TYPE.value)
        return;

      this.layouts = this.layouts.filter(layout => layout.id !== id);

      if (isEmpty(this.layouts)) {
        this.setLayoutSelected();
        await this.getLayouts();
      }

      await this.filterLayoutType();
    },
    /**
     * Modify favorite items list
     *
     * @param {String | Number} id  id of selected layout
     */
    modifyFavorites(id) {
      const index = this.favoriteLayoutIds.findIndex(f => id === f);

      if (index < 0) {
        this.favoriteLayoutIds = insertItemsToArray(this.favoriteLayoutIds, [
          { value: id }
        ]);
      } else {
        this.favoriteLayoutIds = removeItemsFormArray(this.favoriteLayoutIds, [
          { value: id, index }
        ]);
      }
    },
    /**
     * Get favorites from API
     */
    async getFavoritesData() {
      this.favoriteLayoutIds = await this.getFavorites();
    },
    /**
     * Get custom layouts from API
     */
    async getCustomData() {
      this.customLayouts = await this.getCustom();
    },
    /**
     * Get assorted layout categories
     */
    async getAssorted() {
      const assorted = await this.getAssortedLayoutCategories(this.currentPage);
      this.assortedLayoutCategories = assorted;
    },
    /**
     * Filter layout types
     */
    async filterLayoutType() {
      let layoutTypeOpts = [...this.layoutTypesOrigin];

      layoutTypeOpts.splice(
        layoutTypeOpts.length - 1,
        0,
        SAVED_AND_FAVORITES_TYPE
      );

      this.layoutTypes = layoutTypeOpts;

      const assortedType = this.layoutTypes.filter(
        l => l.value === PRINT_LAYOUT_TYPES.ASSORTED.value
      )[0];

      assortedType.subItems = this.assortedLayoutCategories.map(
        ({ id, name }) => ({
          id,
          name,
          value: id,
          shortName: `Assorted: ${name}`
        })
      );
    },
    /**
     * Get layout types
     */
    getLayoutTypes() {
      this.layoutTypesOrigin = Object.values(PRINT_LAYOUT_TYPES).map(lt => ({
        ...lt,
        subItems: []
      }));

      this.layoutTypes = this.layoutTypesOrigin;
    },
    /**
     * Get page type selected value
     *
     * @param   {Object}  selectedData  selected page type data
     * @returns {Object}                page type value
     */
    getSelectedType(selectedData) {
      if (isEmpty(selectedData)) return { sub: '' };

      return { value: selectedData.value, sub: selectedData.sub?.value };
    },
    /**
     * Get layout from API
     */
    async getLayouts() {
      if (isEmpty(this.layoutTypeSelected)) return;
      const typeValue = this.layoutTypeSelected.value;
      const subValue = this.layoutTypeSelected.sub;

      const isAssorted = typeValue === PRINT_LAYOUT_TYPES.ASSORTED.value;

      if (isAssorted) {
        this.layouts = await this.getAssortedLayouts(
          subValue,
          this.currentPageAssortedLayout,
          false
        );
        this.isLoadingLayouts = true;
        const tabLayouts = this.layouts.filter(
          l => l.pageType === this.tabType
        );
        if (tabLayouts.length === 0 && this.layouts.length === this.perPages) {
          this.onLoadMoreLayouts(true);
        }
        return;
      }

      if (typeValue === SAVED_AND_FAVORITES_TYPE.value) {
        this.layouts = await this.getPrintLayouts(
          this.themeSelected?.id,
          typeValue
        );
        this.isLoadingLayouts = true;
        if (this.layouts.length < this.perPages) this.currentPageFavLayout = -1;
        const tabLayouts = this.layouts.filter(
          l => l.pageType === this.tabType
        );
        if (tabLayouts.length === 0) {
          this.loadMoreSavAndFavLayouts();
        }
        return;
      }

      this.layouts =
        this.tabType === LAYOUT_PAGE_TYPE.SINGLE_PAGE.id
          ? await this.getPrintLayouts(
              this.themeSelected?.id,
              typeValue,
              this.tabType,
              this.currentPageSingleLayout
            )
          : await this.getPrintLayouts(
              this.themeSelected?.id,
              typeValue,
              this.tabType,
              this.currentPageSpreadLayout
            );
      this.isLoadingLayouts = true;
      // if layout type is ALL => do not load  extra layouts of other themes
      if (typeValue === PRINT_LAYOUT_TYPES.ALL.value) return;

      // load more layout of the other themes
      const { extraLayouts, isContinue } = await this.getPrintLayoutByType(
        this.themeSelected?.id,
        this.layoutTypeSelected?.value,
        this.currentPageExtraLayouts,
        false
      );
      this.isLoadingExtraLayouts = true;
      this.extraLayouts = extraLayouts;
      const layoutType =
        this.tabType === LAYOUT_PAGE_TYPE.FULL_PAGE.id
          ? LAYOUT_PAGE_TYPE.FULL_PAGE.type
          : LAYOUT_PAGE_TYPE.SINGLE_PAGE.type;
      const tabLayouts = extraLayouts.filter(l => l.type === layoutType);
      if (tabLayouts.length === 0 && isContinue) {
        this.onLoadMoreExtraLayouts(true);
      }
    },
    /**
     * To get favorites and custom layouts
     *
     * @returns array of layouts
     */
    async getFavAndCustomLayouts() {
      const favLayouts = await this.getFavoriteLayouts();
      return [...favLayouts, ...this.customLayouts];
    },
    async onLoadMoreCategory() {
      if (this.currentPage > 0) {
        this.currentPage++;
        const addAssortedCategories = await this.getAssortedLayoutCategories(
          this.currentPage,
          true
        );
        if (addAssortedCategories.length < this.perPages) {
          this.isLoadingCategory = false;
          this.currentPage = -1;
        }
        this.assortedLayoutCategories = [
          ...this.assortedLayoutCategories,
          ...addAssortedCategories
        ];
        this.assortedLayoutCategories.sort((item1, item2) =>
          item1.name.localeCompare(item2.name)
        );
        await this.filterLayoutType();
      }
    },
    async onChangeTab(tab) {
      this.tabType = tab === 0 ? 1 : 0;
      this.isLoadingLayouts = true;
      const layoutTab = this.layouts.filter(l => l.pageType === this.tabType);
      if (isEmpty(layoutTab)) {
        if (this.layoutTypeSelected.value < 0) {
          await this.onLoadMoreLayouts(true);
        } else {
          const layouts =
            this.tabType === LAYOUT_PAGE_TYPE.SINGLE_PAGE.id
              ? await this.getPrintLayouts(
                  this.themeSelected?.id,
                  this.layoutTypeSelected.value,
                  this.tabType,
                  this.currentPageSingleLayout
                )
              : await this.getPrintLayouts(
                  this.themeSelected?.id,
                  this.layoutTypeSelected.value,
                  this.tabType,
                  this.currentPageSpreadLayout
                );
          this.layouts = [...this.layouts, ...layouts];
        }
      }
    },
    async onLoadMoreExtraLayouts(needFreeze) {
      if (isEmpty(this.layoutTypeSelected)) return;
      const hideSpiner = needFreeze ? false : true;
      const layoutType =
        this.tabType === LAYOUT_PAGE_TYPE.FULL_PAGE.id
          ? LAYOUT_PAGE_TYPE.FULL_PAGE.type
          : LAYOUT_PAGE_TYPE.SINGLE_PAGE.type;
      if (this.currentPageExtraLayouts > 0) {
        this.currentPageExtraLayouts++;
        const { extraLayouts, isContinue } = await this.getPrintLayoutByType(
          this.themeSelected?.id,
          this.layoutTypeSelected?.value,
          this.currentPageExtraLayouts,
          hideSpiner
        );
        if (!isContinue) {
          this.currentPageExtraLayouts = -1;
          this.isLoadingExtraLayouts = false;
        }
        this.extraLayouts = [...this.extraLayouts, ...extraLayouts];
        const tabLayouts = extraLayouts.filter(l => l.type === layoutType);
        //Because There are two tab spread and single so data return in 1 page can < 4.
        //if data < 4 can't get scroll event to call next page API.
        if (tabLayouts.length < 4) {
          this.extraLayouts.length === 0
            ? this.onLoadMoreExtraLayouts(true)
            : this.onLoadMoreExtraLayouts(false);
        }
      }
    },
    async onLoadMoreLayouts(needFreeze) {
      const hideSpiner = needFreeze ? false : true;
      const subValue = this.layoutTypeSelected.sub;
      const isAssorted =
        this.layoutTypeSelected.value === PRINT_LAYOUT_TYPES.ASSORTED.value;
      const isSavAndFav =
        this.layoutTypeSelected.value === SAVED_AND_FAVORITES_TYPE.value;
      if (isAssorted) {
        await this.loadMoreAssortedLayouts(subValue, hideSpiner);
        return;
      }
      if (isSavAndFav) {
        await this.loadMoreSavAndFavLayouts(hideSpiner);
        return;
      }
      await this.loadMoreLayoutsByTheme(hideSpiner);
    },

    async loadMoreLayoutsByTheme(hideSpiner) {
      const typeValue = this.layoutTypeSelected.value;
      if (this.tabType === LAYOUT_PAGE_TYPE.SINGLE_PAGE.id) {
        if (this.currentPageSingleLayout < 0) {
          this.isLoadingLayouts = false;
          return;
        }
        this.currentPageSingleLayout++;
        const layouts = await this.getPrintLayouts(
          this.themeSelected?.id,
          typeValue,
          this.tabType,
          this.currentPageSingleLayout,
          hideSpiner
        );
        this.layouts = [...this.layouts, ...layouts];
        if (layouts.length < this.perPages) {
          this.isLoadingLayouts = false;
          this.currentPageSingleLayout = -1;
        }
      } else {
        if (this.currentPageSpreadLayout < 0) {
          this.isLoadingLayouts = false;
          return;
        }
        this.currentPageSpreadLayout++;
        const layouts = await this.getPrintLayouts(
          this.themeSelected?.id,
          typeValue,
          this.tabType,
          this.currentPageSpreadLayout,
          hideSpiner
        );
        this.layouts = [...this.layouts, ...layouts];
        if (layouts.length < this.perPages) {
          this.isLoadingLayouts = false;
          this.currentPageSpreadLayout = -1;
        }
      }
    },
    async loadMoreAssortedLayouts(subValue, hideSpiner) {
      if (this.currentPageAssortedLayout < 0) {
        this.isLoadingLayouts = false;
        return;
      }
      this.currentPageAssortedLayout++;
      const assortedLayouts = await this.getAssortedLayouts(
        subValue,
        this.currentPageAssortedLayout,
        hideSpiner
      );
      if (assortedLayouts.length < this.perPages) {
        this.isLoadingLayouts = false;
        this.currentPageAssortedLayout = -1;
      }
      this.layouts = [...this.layouts, ...assortedLayouts];
      const tabLayouts = assortedLayouts.filter(
        l => l.pageType === this.tabType
      );
      const layouts = this.layouts.filter(l => l.pageType === this.tabType);
      //Because There are two tab spread and single/layout_type = null so data return in 1 page can < 4.
      //if data < 4 can't get scroll event to call next page API.
      if (tabLayouts.length < 4) {
        layouts.length === 0
          ? this.onLoadMoreLayouts(true)
          : this.onLoadMoreLayouts(false);
      }
    },
    async loadMoreSavAndFavLayouts(hideSpiner) {
      if (
        this.currentPageFavLayout < 0 &&
        this.currentPageUserSingleLayouts < 0 &&
        this.currentPageUserSpreadLayouts < 0
      ) {
        this.isLoadingLayouts = false;
        return;
      }
      if (this.currentPageFavLayout < 0) {
        await this.loadMoreUserLayouts(hideSpiner);
        return;
      }
      this.currentPageFavLayout++;
      const extraFavLayouts = await this.getFavoriteLayouts(
        this.currentPageFavLayout,
        hideSpiner
      );
      this.layouts = [...this.layouts, ...extraFavLayouts];
      if (extraFavLayouts.length < this.perPages) {
        this.currentPageFavLayout = -1;
      }
      const tabLayouts = extraFavLayouts.filter(
        l => l.pageType === this.tabType
      );
      const layouts = this.layouts.filter(l => l.pageType === this.tabType);
      //Because There are two tab spread and single so data return in 1 page can < 4.
      //if data < 4 can't get scroll event to call next page API.
      if (tabLayouts.length < 4) {
        layouts.length === 0
          ? this.loadMoreSavAndFavLayouts(false)
          : this.loadMoreSavAndFavLayouts(true);
      }
    },
    async loadMoreUserLayouts(hideSpiner) {
      let userLayouts = [];
      if (this.tabType === LAYOUT_PAGE_TYPE.SINGLE_PAGE.id) {
        if (this.currentPageUserSingleLayouts < 0) {
          this.isLoadingLayouts = false;
          return;
        }
        this.currentPageUserSingleLayouts++;
        userLayouts = await this.getCustom(
          this.tabType,
          this.currentPageUserSingleLayouts,
          hideSpiner
        );
        if (userLayouts.length < this.perPages) {
          this.isLoadingLayouts = false;
          this.currentPageUserSingleLayouts = -1;
        }
      } else {
        if (this.currentPageUserSpreadLayouts < 0) {
          this.isLoadingLayouts = false;
          return;
        }
        this.currentPageUserSpreadLayouts++;
        userLayouts = await this.getCustom(
          this.tabType,
          this.currentPageUserSpreadLayouts,
          hideSpiner
        );
        if (userLayouts.length < this.perPages) {
          this.isLoadingLayouts = false;
          this.currentPageUserSpreadLayouts = -1;
        }
      }
      userLayouts.forEach(item => {
        const index = this.layouts.findIndex(layout => layout.id === item.id);
        if (index < 0) this.layouts.push(item);
      });
      this.layouts.sort((item1, item2) => {
        if (item1.isCustom && item2.isCustom) return item1.id - item2.id;
      });
    },
    async onLoadMoreTheme() {
      if (this.currentPageTheme < 0) {
        this.isLoadingTheme = false;
        return;
      }
      this.currentPageTheme++;
      const themes = await getThemesApi(false, this.currentPageTheme, true);
      this.themesOptions = [...this.themesOptions, ...themes];
      this.themesOptions.sort((item1, item2) => item1.id - item2.id);
      if (themes.length < this.perPages) {
        this.isLoadingTheme = false;
        this.currentPageTheme = -1;
      }
    }
  }
};
